
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useSelector } from "react-redux";
import {
  AlertCircle,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Filter,
  Mail,
  RefreshCw,
  Search,
  TrendingUp,
  User,
  Users,
  Wallet,
  X,
  XCircle,
} from "lucide-react";

const Qazanc = () => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  const [filters, setFilters] = useState({
    earnedAboveZero: null,
    invitedAboveZero: null,
  });

  const [expandedRow, setExpandedRow] = useState(null);

  const [errors, setErrors] = useState(null);
  const [tableError, setTableError] = useState(null);

  const [stats, setStats] = useState(null);

  const [retryStats, setRetryStats] = useState(0);
  const [retryTable, setRetryTable] = useState(0);

  const { userInfo } = useSelector((state) => state.auth);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // ---------------------------------------------------------
  // XƏTA MƏTNİNİ HAZIRLA
  // ---------------------------------------------------------
  const getErrorMessage = (error, defaultMessage) => {
    if (!error) {
      return defaultMessage;
    }

    if (error.response) {
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.details;

      if (serverMessage) {
        return String(serverMessage);
      }

      switch (error.response.status) {
        case 400:
          return "Göndərilən sorğu düzgün deyil.";
        case 401:
          return "Sessiyanızın müddəti bitib. Zəhmət olmasa yenidən daxil olun.";
        case 403:
          return "Bu məlumatlara baxmaq üçün icazəniz yoxdur.";
        case 404:
          return "Tələb olunan məlumat tapılmadı.";
        case 408:
          return "Serverdən cavab almaq çox uzun çəkdi.";
        case 429:
          return "Çox sayda sorğu göndərildi. Bir qədər sonra yenidən cəhd edin.";
        default:
          if (error.response.status >= 500) {
            return `Server xətası baş verdi. Xəta kodu: ${error.response.status}`;
          }

          return `Sorğu zamanı xəta baş verdi. Xəta kodu: ${error.response.status}`;
      }
    }

    if (error.request) {
      return "Serverə qoşulmaq mümkün olmadı. İnternet bağlantınızı və serverin işlək vəziyyətdə olduğunu yoxlayın.";
    }

    if (error.message) {
      return error.message;
    }

    return defaultMessage;
  };

  // ---------------------------------------------------------
  // ŞƏXSİ QAZANC STATİSTİKALARINI GƏTİR
  // ---------------------------------------------------------
  const fetchStats = useCallback(async () => {
    if (!userInfo?.referralCode) {
      setErrors(
        "İstifadəçinin referans kodu tapılmadı. Qazanc məlumatlarını göstərmək mümkün deyil."
      );
      setStats(null);
      setStatsLoading(false);
      return;
    }

    if (!API_BASE_URL) {
      setErrors(
        "API serverinin ünvanı müəyyən edilməyib. REACT_APP_API_BASE_URL dəyişənini yoxlayın."
      );
      setStats(null);
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);
    setErrors(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/users/referral-stats/${userInfo.referralCode}`,
        {
          withCredentials: true,
        }
      );

      setStats(response.data || null);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Qazanc statistikaları alınarkən xəta baş verdi."
      );

      setErrors(errorMessage);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [userInfo?.referralCode, API_BASE_URL]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, retryStats]);

  // ---------------------------------------------------------
  // BÜTÜN MÜKAFAT MƏLUMATLARINI GƏTİR
  // ---------------------------------------------------------
  const fetchData = useCallback(async () => {
    if (!API_BASE_URL) {
      setTableError(
        "API serverinin ünvanı müəyyən edilməyib. REACT_APP_API_BASE_URL dəyişənini yoxlayın."
      );
      setAllData([]);
      setFilteredData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setTableError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/mane`, {
        withCredentials: true,
      });

      const result = Array.isArray(response.data)
        ? response.data
        : [];

      setAllData(result);
      setFilteredData(result);
      setExpandedRow(null);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Mükafat məlumatları alınarkən xəta baş verdi."
      );

      setTableError(errorMessage);
      setAllData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData, retryTable]);

  // ---------------------------------------------------------
  // FİLTRLƏR
  // ---------------------------------------------------------
  useEffect(() => {
    let tempData = [...allData];

    const searchLower = search.trim().toLowerCase();

    if (searchLower) {
      tempData = tempData.filter((item) => {
        const name = String(item?.referrerName || "").toLowerCase();

        const referralCode = String(
          item?.referrerReferralCode || ""
        ).toLowerCase();

        return (
          name.includes(searchLower) ||
          referralCode.includes(searchLower)
        );
      });
    }

    if (filters.earnedAboveZero !== null) {
      tempData = tempData.filter((item) => {
        const earned = Number(item?.totalEarned || 0);

        return filters.earnedAboveZero
          ? earned > 0
          : earned <= 0;
      });
    }

    if (filters.invitedAboveZero !== null) {
      tempData = tempData.filter((item) => {
        const invited = Number(item?.totalInvited || 0);

        return filters.invitedAboveZero
          ? invited > 0
          : invited <= 0;
      });
    }

    setFilteredData(tempData);
  }, [search, filters, allData]);

  // ---------------------------------------------------------
  // SƏTRİ AÇ / BAĞLA
  // ---------------------------------------------------------
  const toggleRow = (index) => {
    setExpandedRow((previous) =>
      previous === index ? null : index
    );
  };

  // ---------------------------------------------------------
  // FİLTR DÜYMƏSİ
  // ---------------------------------------------------------
  const toggleFilter = (key, value) => {
    setFilters((previous) => ({
      ...previous,
      [key]: previous[key] === value ? null : value,
    }));
  };

  // ---------------------------------------------------------
  // AXTARIŞ TƏKLİFİ
  // ---------------------------------------------------------
  const handleSuggestionClick = (referrerName) => {
    setSearch(referrerName);
  };

  // ---------------------------------------------------------
  // FİLTRLƏRİ TƏMİZLƏ
  // ---------------------------------------------------------
  const clearFilters = () => {
    setFilters({
      earnedAboveZero: null,
      invitedAboveZero: null,
    });

    setSearch("");
    setExpandedRow(null);
  };

  // ---------------------------------------------------------
  // AKTİV FİLTR SAYI
  // ---------------------------------------------------------
  const activeFilterCount = useMemo(() => {
    let count = 0;

    if (filters.earnedAboveZero !== null) {
      count++;
    }

    if (filters.invitedAboveZero !== null) {
      count++;
    }

    if (search.trim()) {
      count++;
    }

    return count;
  }, [filters, search]);

  // ---------------------------------------------------------
  // MƏBLƏĞ FORMATLAMA
  // ---------------------------------------------------------
  const formatAmount = (value) => {
    const amount = Number(value ?? 0);

    if (Number.isNaN(amount)) {
      return "0.00";
    }

    return amount.toFixed(2);
  };

  // ---------------------------------------------------------
  // AXTARIŞ TƏKLİFLƏRİ
  // ---------------------------------------------------------
  const suggestions = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return [];
    }

    return allData
      .filter((item) => {
        const name = String(
          item?.referrerName || ""
        ).toLowerCase();

        const referralCode = String(
          item?.referrerReferralCode || ""
        ).toLowerCase();

        return (
          name.includes(query) ||
          referralCode.includes(query)
        );
      })
      .slice(0, 10);
  }, [allData, search]);

  // ---------------------------------------------------------
  // FİLTR DÜYMƏ KOMPONENTİ
  // ---------------------------------------------------------
  const filterButton = (label, key, value, icon) => {
    const isActive = filters[key] === value;

    return (
      <button
        type="button"
        onClick={() => toggleFilter(key, value)}
        className={`
          inline-flex
          items-center
          justify-center
          gap-2
          px-4
          py-2.5
          rounded-xl
          text-sm
          font-semibold
          border
          transition-all
          duration-200
          ${
            isActive
              ? "bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-500/20"
              : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-700"
          }
        `}
      >
        {icon}
        {label}
      </button>
    );
  };

  // ---------------------------------------------------------
  // İLK YÜKLƏNMƏ EKRANI
  // ---------------------------------------------------------
  if (loading && statsLoading && !stats && allData.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-white/10 shadow-2xl p-8 text-center">

            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 mb-5">
              <TrendingUp className="w-8 h-8 text-white animate-pulse" />
            </div>

            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Qazanc məlumatları yüklənir
            </h2>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Şəxsi qazanc statistikalarınız və mükafat məlumatları
              hazırlanır. Zəhmət olmasa gözləyin...
            </p>

            <div className="mt-6 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full w-1/2 bg-gradient-to-r from-indigo-500 to-violet-600 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white px-4 sm:px-6 lg:px-8 py-6 sm:py-10 transition-colors duration-300">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* =================================================
            BAŞLIQ
        ================================================= */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 p-6 sm:p-8 text-white shadow-2xl">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-24 w-80 h-80 bg-fuchsia-400/10 rounded-full blur-3xl" />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-semibold mb-3">
                <CircleDollarSign className="w-4 h-4" />
                Qazanc paneli
              </div>

              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">
                Dəvət qazancı
              </h1>

              <p className="mt-2 text-sm sm:text-base text-white/75 max-w-2xl">
                Dəvətlərinizdən əldə etdiyiniz qazancı, istifadəçi sayını
                və dövrlər üzrə gəlirlərinizi burada izləyə bilərsiniz.
              </p>
            </div>

            <div className="w-14 h-14 shrink-0 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
              <Wallet className="w-7 h-7" />
            </div>
          </div>
        </div>

        {/* =================================================
            ŞƏXSİ STATİSTİKA PANELİ
        ================================================= */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-white/10 shadow-xl overflow-hidden">

          <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl font-bold">
                    Şəxsi qazanc statistikası
                  </h2>

                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                    Dəvətləriniz üzrə ümumi göstəricilər
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setRetryStats((value) => value + 1)}
                disabled={statsLoading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-sm"
              >
                <RefreshCw
                  className={`w-4 h-4 ${
                    statsLoading ? "animate-spin" : ""
                  }`}
                />
                Yenilə
              </button>
            </div>
          </div>

          <div className="p-5 sm:p-6">

            {/* STATİSTİKA YÜKLƏNİR */}
            {statsLoading ? (
              <div className="py-14 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mb-4">
                  <RefreshCw className="w-7 h-7 text-indigo-600 dark:text-indigo-400 animate-spin" />
                </div>

                <h3 className="text-lg font-bold">
                  Qazanc statistikaları yüklənir
                </h3>

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  Şəxsi qazanc məlumatlarınız serverdən alınır. Zəhmət
                  olmasa gözləyin...
                </p>
              </div>
            ) : errors ? (
              /* STATİSTİKA XƏTASI */
              <div className="rounded-2xl border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10 p-5">
                <div className="flex items-start gap-3">

                  <div className="w-10 h-10 shrink-0 rounded-xl bg-red-100 dark:bg-red-500/10 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>

                  <div className="min-w-0">
                    <h3 className="font-bold text-red-800 dark:text-red-300">
                      Qazanc məlumatları yüklənmədi
                    </h3>

                    <p className="mt-2 text-sm text-red-700 dark:text-red-400 break-words">
                      <span className="font-bold">
                        Xətanın səbəbi:
                      </span>{" "}
                      {errors}
                    </p>

                    <button
                      type="button"
                      onClick={() => setRetryStats((value) => value + 1)}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Yenidən cəhd et
                    </button>
                  </div>
                </div>
              </div>
            ) : !stats ? (
              /* STATİSTİKA YOXDUR */
              <div className="rounded-2xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 p-8 text-center">
                <CircleDollarSign className="w-9 h-9 mx-auto text-amber-600 dark:text-amber-400 mb-4" />

                <h3 className="text-lg font-bold text-amber-800 dark:text-amber-300">
                  Qazanc məlumatı mövcud deyil
                </h3>

                <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
                  Hazırda hesabınız üçün göstəriləcək qazanc statistikası
                  tapılmadı.
                </p>
              </div>
            ) : (
              <>
                {/* =================================================
                    STATİSTİKA KARTLARI
                ================================================= */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

                  {/* AD */}
                  <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-100 dark:from-indigo-500/10 dark:to-violet-500/10 border border-indigo-100 dark:border-indigo-500/20 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                          İstifadəçi
                        </p>

                        <p className="mt-2 text-xl font-black break-words">
                          {stats.referrerName || "Ad göstərilməyib"}
                        </p>
                      </div>

                      <div className="w-11 h-11 shrink-0 rounded-xl bg-white/70 dark:bg-white/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                    </div>
                  </div>

                  {/* DƏVƏT */}
                  <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-100 dark:from-blue-500/10 dark:to-cyan-500/10 border border-blue-100 dark:border-blue-500/20 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                          Ümumi dəvət sayı
                        </p>

                        <p className="mt-2 text-2xl font-black">
                          {Number(stats.totalInvited || 0)} nəfər
                        </p>
                      </div>

                      <div className="w-11 h-11 shrink-0 rounded-xl bg-white/70 dark:bg-white/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                  </div>

                  {/* QAZANC */}
                  <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-500/10 dark:to-green-500/10 border border-emerald-100 dark:border-emerald-500/20 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                          Ümumi qazanc
                        </p>

                        <p className="mt-2 text-2xl font-black text-emerald-600 dark:text-emerald-400">
                          {formatAmount(stats.totalEarned)} ₼
                        </p>
                      </div>

                      <div className="w-11 h-11 shrink-0 rounded-xl bg-white/70 dark:bg-white/10 flex items-center justify-center">
                        <CircleDollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    QRAFİK
                ================================================= */}
                <div className="mt-8">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>

                    <div>
                      <h3 className="text-lg sm:text-xl font-bold">
                        Həftəlik qazanc qrafiki
                      </h3>

                      <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                        Dövrlər üzrə qazanc göstəriciləri
                      </p>
                    </div>
                  </div>

                  {Array.isArray(stats.periodEarnings) &&
                  stats.periodEarnings.length > 0 ? (
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-white/10 p-4 sm:p-6">
                      <ResponsiveContainer
                        width="100%"
                        height={320}
                      >
                        <BarChart
                          data={stats.periodEarnings}
                          margin={{
                            top: 10,
                            right: 20,
                            left: 0,
                            bottom: 50,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            className="opacity-40"
                          />

                          <XAxis
                            dataKey="periodLabel"
                            angle={-20}
                            textAnchor="end"
                            interval={0}
                            height={70}
                          />

                          <YAxis />

                          <Tooltip
                            formatter={(value) => [
                              `${formatAmount(value)} ₼`,
                              "Qazanc",
                            ]}
                            labelFormatter={(label) =>
                              `Dövr: ${label}`
                            }
                            contentStyle={{
                              borderRadius: "12px",
                              border: "1px solid #e2e8f0",
                            }}
                          />

                          <Bar
                            dataKey="earned"
                            fill="#6366f1"
                            radius={[8, 8, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/50 p-10 text-center">
                      <BarChart3 className="w-10 h-10 mx-auto text-slate-400 mb-4" />

                      <h3 className="text-lg font-bold">
                        Qrafik məlumatı yoxdur
                      </h3>

                      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                        Hazırda göstəriləcək qazanc dövrü mövcud deyil.
                      </p>
                    </div>
                  )}
                </div>

                {/* =================================================
                    DÖVR CƏDVƏLİ
                ================================================= */}
                <div className="mt-8">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                      <CalendarDays className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                    </div>

                    <div>
                      <h3 className="text-lg sm:text-xl font-bold">
                        Qazanc dövrləri
                      </h3>

                      <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                        Hər dövr üzrə qazanc məlumatları
                      </p>
                    </div>
                  </div>

                  {Array.isArray(stats.periodEarnings) &&
                  stats.periodEarnings.length > 0 ? (
                    <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-white/10">
                      <table className="w-full min-w-[650px] text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-800">
                          <tr className="text-left">
                            <th className="px-5 py-4 font-bold">
                              Dövr
                            </th>

                            <th className="px-5 py-4 font-bold">
                              İstifadəçi sayı
                            </th>

                            <th className="px-5 py-4 font-bold">
                              Qazanc
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {stats.periodEarnings.map(
                            (period, index) => (
                              <tr
                                key={`${period?.periodLabel || "dovr"}-${index}`}
                                className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-white/5 transition"
                              >
                                <td className="px-5 py-4 font-semibold">
                                  {period?.periodLabel ||
                                    "Dövr göstərilməyib"}
                                </td>

                                <td className="px-5 py-4">
                                  {Number(
                                    period?.userCount || 0
                                  )}
                                </td>

                                <td className="px-5 py-4 font-bold text-emerald-600 dark:text-emerald-400">
                                  {formatAmount(
                                    period?.earned
                                  )}{" "}
                                  ₼
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-8 text-center">
                      <CalendarDays className="w-9 h-9 mx-auto text-slate-400 mb-3" />

                      <p className="font-semibold">
                        Qazanc dövrü məlumatı yoxdur.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        {/* =================================================
            MÜKAFAT HESABATI BAŞLIĞI
        ================================================= */}
        <section>
          <div className="mb-6">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
                <CircleDollarSign className="w-5 h-5 text-white" />
              </div>

              <div>
                <h2 className="text-2xl sm:text-3xl font-black">
                  Qollar üzrə mükafat hesabatı
                </h2>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  İstifadəçilərin dəvət və qazanc məlumatları
                </p>
              </div>
            </div>
          </div>

          {/* =================================================
              AXTARIŞ
          ================================================= */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-white/10 shadow-xl p-4 sm:p-6">

            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />

              <input
                type="text"
                placeholder="İstifadəçi adı və ya referans kodu ilə axtar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-12 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 transition placeholder:text-slate-400"
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center transition"
                  aria-label="Axtarışı təmizlə"
                >
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              )}

              {/* AXTARIŞ TƏKLİFLƏRİ */}
              {search && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-white/10 shadow-2xl z-10 overflow-hidden">

                  {suggestions.map((item, index) => (
                    <button
                      type="button"
                      key={`${item?.referrerReferralCode || index}-${index}`}
                      onClick={() =>
                        handleSuggestionClick(
                          item?.referrerName || ""
                        )
                      }
                      className="w-full text-left px-4 py-3 hover:bg-indigo-50 dark:hover:bg-slate-800 transition border-b last:border-b-0 border-slate-100 dark:border-slate-800"
                    >
                      <div className="flex items-center gap-3">

                        <div className="w-9 h-9 shrink-0 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        </div>

                        <div className="min-w-0">
                          <p className="font-semibold truncate">
                            {item?.referrerName ||
                              "Ad göstərilməyib"}
                          </p>

                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {item?.referrerEmail ||
                              "E-poçt göstərilməyib"}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* TƏKLİF TAPILMADI */}
              {search && suggestions.length === 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-white/10 shadow-2xl z-10 p-4">
                  <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                    <Search className="w-5 h-5" />
                    <span>
                      Bu axtarışa uyğun istifadəçi tapılmadı.
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* =================================================
                FİLTRLƏR
            ================================================= */}
            <div className="mt-5 pt-5 border-t border-slate-200 dark:border-white/10">

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-indigo-500" />

                  <span className="text-sm font-bold">
                    Filtrlər
                  </span>

                  {activeFilterCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 text-xs font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </div>

                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    Bütün filtrləri təmizlə
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2.5">
                {filterButton(
                  "Qazancı olanlar",
                  "earnedAboveZero",
                  true,
                  <CheckCircle2 className="w-4 h-4" />
                )}

                {filterButton(
                  "Qazancı olmayanlar",
                  "earnedAboveZero",
                  false,
                  <XCircle className="w-4 h-4" />
                )}

                {filterButton(
                  "Dəvət edənlər",
                  "invitedAboveZero",
                  true,
                  <Users className="w-4 h-4" />
                )}

                {filterButton(
                  "Dəvət etməyənlər",
                  "invitedAboveZero",
                  false,
                  <User className="w-4 h-4" />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* =================================================
            MÜKAFAT CƏDVƏLİ
        ================================================= */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-white/10 shadow-xl overflow-hidden">

          <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

              <div>
                <h3 className="text-lg sm:text-xl font-bold">
                  Mükafat məlumatları
                </h3>

                <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Dəvət və qazanc göstəricilərinin ətraflı hesabatı
                </p>
              </div>

              {!loading && !tableError && (
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm">
                  <Users className="w-4 h-4 text-indigo-500" />
                  <span className="font-bold">
                    {filteredData.length}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">
                    istifadəçi
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 sm:p-6">

            {/* =================================================
                CƏDVƏL YÜKLƏNİR
            ================================================= */}
            {loading ? (
              <div className="py-16 text-center">

                <div className="w-16 h-16 mx-auto rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mb-5">
                  <RefreshCw className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
                </div>

                <h3 className="text-lg font-bold">
                  Mükafat məlumatları yüklənir
                </h3>

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                  İstifadəçilərin qazanc və dəvət məlumatları serverdən
                  alınır. Zəhmət olmasa gözləyin...
                </p>

                <div className="mt-5 mx-auto w-full max-w-sm h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div className="h-full w-1/2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-600 animate-pulse" />
                </div>
              </div>
            ) : tableError ? (
              /* =================================================
                  CƏDVƏL XƏTASI
              ================================================= */
              <div className="rounded-2xl border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10 p-6">
                <div className="flex items-start gap-4">

                  <div className="w-11 h-11 shrink-0 rounded-xl bg-red-100 dark:bg-red-500/10 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>

                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-red-800 dark:text-red-300">
                      Mükafat məlumatları yüklənmədi
                    </h3>

                    <p className="mt-2 text-sm text-red-700 dark:text-red-400 break-words">
                      <span className="font-bold">
                        Xətanın səbəbi:
                      </span>{" "}
                      {tableError}
                    </p>

                    <p className="mt-2 text-xs text-red-600/80 dark:text-red-400/80">
                      Server bağlantısını, giriş icazənizi və ya API
                      məlumatlarını yoxlamaq lazım ola bilər.
                    </p>

                    <button
                      type="button"
                      onClick={() =>
                        setRetryTable((value) => value + 1)
                      }
                      className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Yenidən cəhd et
                    </button>
                  </div>
                </div>
              </div>
            ) : allData.length === 0 ? (
              /* =================================================
                  MƏLUMAT YOXDUR
              ================================================= */
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/50 p-12 text-center">

                <Users className="w-11 h-11 mx-auto text-slate-400 mb-4" />

                <h3 className="text-lg font-bold">
                  Mükafat məlumatı yoxdur
                </h3>

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                  Hazırda göstəriləcək istifadəçi və ya mükafat məlumatı
                  mövcud deyil.
                </p>
              </div>
            ) : filteredData.length === 0 ? (
              /* =================================================
                  FİLTR NƏTİCƏSİ YOXDUR
              ================================================= */
              <div className="rounded-2xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 p-12 text-center">

                <Search className="w-11 h-11 mx-auto text-amber-500 mb-4" />

                <h3 className="text-lg font-bold text-amber-800 dark:text-amber-300">
                  Nəticə tapılmadı
                </h3>

                <p className="mt-2 text-sm text-amber-700 dark:text-amber-400 max-w-md mx-auto">
                  Seçdiyiniz filtr və ya axtarış meyarlarına uyğun istifadəçi
                  tapılmadı.
                </p>

                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition"
                >
                  <RefreshCw className="w-4 h-4" />
                  Filtrləri təmizlə
                </button>
              </div>
            ) : (
              /* =================================================
                  CƏDVƏL
              ================================================= */
              <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-white/10">

                <div className="max-h-[650px] overflow-y-auto">

                  <table className="min-w-[1050px] w-full text-sm">

                    <thead className="sticky top-0 z-10 bg-white dark:bg-slate-900 shadow-md">
                      <tr className="text-left">

                        <th className="px-5 py-4 font-bold">
                          №
                        </th>

                        <th className="px-5 py-4 font-bold">
                          İstifadəçi
                        </th>

                        <th className="px-5 py-4 font-bold">
                          E-poçt
                        </th>

                        <th className="px-5 py-4 font-bold">
                          Ümumi qazanc
                        </th>

                        <th className="px-5 py-4 font-bold">
                          Dəvət edilən
                        </th>

                        <th className="px-5 py-4 font-bold">
                          Ödəniş edənlər
                        </th>

                        <th className="px-5 py-4 font-bold">
                          Dövr üzrə qazanc
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">

                      {filteredData.map((user, index) => {
                        const isExpanded =
                          expandedRow === index;

                        const periodEarnings =
                          Array.isArray(user?.periodEarnings)
                            ? user.periodEarnings
                            : [];

                        return (
                          <React.Fragment
                            key={
                              user?._id ||
                              user?.referrerReferralCode ||
                              `${user?.referrerEmail}-${index}`
                            }
                          >
                            {/* ƏSAS SƏTİR */}
                            <tr
                              onClick={() =>
                                toggleRow(index)
                              }
                              className="cursor-pointer hover:bg-indigo-50/60 dark:hover:bg-white/5 transition"
                            >
                              <td className="px-5 py-4 font-bold text-slate-500 dark:text-slate-400">
                                {index + 1}
                              </td>

                              <td className="px-5 py-4">
                                <div className="flex items-center gap-3">

                                  <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white">
                                    <User className="w-4 h-4" />
                                  </div>

                                  <div className="min-w-0">
                                    <p className="font-bold truncate max-w-[180px]">
                                      {user?.referrerName ||
                                        "Ad göstərilməyib"}
                                    </p>

                                    {user?.referrerReferralCode && (
                                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[180px]">
                                        Kod:{" "}
                                        {
                                          user.referrerReferralCode
                                        }
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td className="px-5 py-4">
                                <div className="flex items-center gap-2">
                                  <Mail className="w-4 h-4 text-slate-400 shrink-0" />

                                  <span className="truncate max-w-[220px]">
                                    {user?.referrerEmail ||
                                      "E-poçt göstərilməyib"}
                                  </span>
                                </div>
                              </td>

                              <td className="px-5 py-4">
                                <span className="inline-flex items-center gap-2 font-bold text-emerald-600 dark:text-emerald-400">
                                  <CircleDollarSign className="w-4 h-4" />
                                  {formatAmount(
                                    user?.totalEarned
                                  )}{" "}
                                  ₼
                                </span>
                              </td>

                              <td className="px-5 py-4">
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 font-semibold">
                                  <Users className="w-4 h-4 text-indigo-500" />
                                  {Number(user?.count || 0)}
                                </span>
                              </td>

                              <td className="px-5 py-4">
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-semibold">
                                  <CheckCircle2 className="w-4 h-4" />
                                  {Number(
                                    user?.totalInvited || 0
                                  )}
                                </span>
                              </td>

                              <td className="px-5 py-4">
                                <span className="inline-flex items-center gap-2 font-semibold text-indigo-600 dark:text-indigo-400">
                                  {isExpanded ? (
                                    <>
                                      <ChevronUp className="w-4 h-4" />
                                      Gizlət
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-4 h-4" />
                                      Göstər
                                    </>
                                  )}
                                </span>
                              </td>
                            </tr>

                            {/* AÇILAN DÖVR MƏLUMATLARI */}
                            {isExpanded && (
                              <tr className="bg-slate-50 dark:bg-slate-800/60">
                                <td
                                  colSpan={7}
                                  className="px-5 py-5"
                                >
                                  <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 p-5">

                                    <div className="flex items-center gap-3 mb-4">
                                      <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
                                        <CalendarDays className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                      </div>

                                      <div>
                                        <h4 className="font-bold">
                                          Dövrlər üzrə qazanc
                                        </h4>

                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                          İstifadəçinin hər dövr üzrə qazanc
                                          məlumatları
                                        </p>
                                      </div>
                                    </div>

                                    {periodEarnings.length > 0 ? (
                                      <div className="space-y-2">
                                        {periodEarnings.map(
                                          (
                                            period,
                                            periodIndex
                                          ) => (
                                            <div
                                              key={`${period?.periodLabel || "dovr"}-${periodIndex}`}
                                              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800"
                                            >
                                              <span className="font-semibold">
                                                {period?.periodLabel ||
                                                  "Dövr göstərilməyib"}
                                              </span>

                                              <span className="font-bold text-emerald-600 dark:text-emerald-400">
                                                {formatAmount(
                                                  period?.earned
                                                )}{" "}
                                                ₼
                                              </span>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    ) : (
                                      <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-5 text-center">
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                          Bu istifadəçi üçün dövr üzrə qazanc
                                          məlumatı yoxdur.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* =================================================
              CƏDVƏLİN ALT HİSSƏSİ
          ================================================= */}
          {!loading &&
            !tableError &&
            filteredData.length > 0 && (
              <div className="px-5 sm:px-6 py-4 border-t border-slate-200 dark:border-white/10 bg-slate-50/70 dark:bg-slate-800/40">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">

                  <p className="text-slate-500 dark:text-slate-400">
                    Göstərilən istifadəçi sayı:{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {filteredData.length}
                    </span>
                  </p>

                  {search && (
                    <p className="text-slate-500 dark:text-slate-400">
                      Axtarış:
                      <span className="ml-1 font-bold text-slate-900 dark:text-white">
                        "{search}"
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}
        </section>
      </div>
    </div>
  );
};

export default Qazanc;

